import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import OsScroll from '../ui/OsScroll';
import EventChip from './EventChip';
import {
  startOfDay,
  addDays,
  eventTouchesDay,
  eventRange,
  isSameDay,
  formatDayLong,
  toDateKey,
} from '../../utils/calendarDates';

const AGENDA_DAYS = 30;

/**
 * Agenda: the next 30 days as a grouped upcoming list (cheap, and the
 * clearest surface for "what's coming" — PLAN §3.5). Days without events are
 * omitted; an empty month shows one friendly empty state.
 */
export default function AgendaView({ focusedDate, events, timeFormat, now = new Date(), onSelectEvent, onEventContextMenu }) {
  const { t } = useTranslation();
  const groups = useMemo(() => {
    const from = startOfDay(focusedDate);
    const out = [];
    for (let i = 0; i < AGENDA_DAYS; i++) {
      const day = addDays(from, i);
      const dayEvents = events
        .filter((e) => eventTouchesDay(e, day))
        .sort((a, b) => {
          if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
          return eventRange(a).start - eventRange(b).start;
        });
      if (dayEvents.length > 0) out.push({ day, dayEvents });
    }
    return out;
  }, [focusedDate, events]);

  return (
    <OsScroll style={{ flex: 1, minHeight: 0 }}>
      <div className="cal-agenda">
        {groups.length === 0 && (
          <div className="cal-agenda-empty">
            {t('calendar.agenda.empty', { count: AGENDA_DAYS })}
          </div>
        )}
        {groups.map(({ day, dayEvents }) => (
          <div key={toDateKey(day)} className="cal-agenda-day">
            <div
              className={`cal-agenda-dayheader${isSameDay(day, now) ? ' cal-agenda-dayheader--today' : ''}`}
            >
              {formatDayLong(day)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {dayEvents.map((event) => (
                <EventChip
                  key={event.uid}
                  event={event}
                  timeFormat={timeFormat}
                  onSelect={onSelectEvent}
                  onEventContextMenu={onEventContextMenu}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </OsScroll>
  );
}
